import type {
  AiActionCapabilityV1,
  CampaignCreationCapabilities,
  ExecutionModeV1,
  SmartCreationBrowserDraft,
  SmartCreationReviewAdGroup,
  SmartCreationReviewCampaign,
  SmartCreationReviewEditChange,
  SmartCreationReviewWorkspace,
  SmartCreationStrategyLane,
} from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconCube,
  IconInfoCircle,
  IconPackage,
  IconRefresh,
  IconTarget,
  IconTypography,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { useIsVendorShop } from "@/hooks/use-shop-account-type";
import { fetchSmartCreationActionCapabilities } from "@/lib/smart-creation-api";
import { ReviewAiActionSpace } from "./ReviewAiActionSpace";
import { ReviewAiModeSelector } from "./ReviewAiModeSelector";
import {
  hasManualBudgetEdits,
  matchingBudgetSuggestion,
  ReviewBudgetDialog,
} from "./ReviewBudgetDialog";
import { ReviewCampaignTable } from "./ReviewCampaignTable";
import { ReviewDeliverySettings } from "./ReviewDeliverySettings";
import { ReviewEntitySheet } from "./ReviewEntitySheet";
import { ReviewNamingRules } from "./ReviewNamingRules";
import { ReviewStructureGraph } from "./ReviewStructureGraph";
import { ReviewTargetDialog } from "./ReviewTargetDialog";
import type { ReviewChangeHandler } from "./review-change-handler";

export interface SmartCreationDraftReviewProps {
  plan?: SmartCreationBrowserDraft;
  creationCapabilities?: CampaignCreationCapabilities;
  workspace: SmartCreationReviewWorkspace;
  disabled?: boolean;
  applying?: boolean;
  refreshingPreview?: boolean;
  confirming?: boolean;
  onApplyChanges: ReviewChangeHandler;
  onApplyOptimistic?: ReviewChangeHandler;
  onRefreshPreview(): void;
  onConfirm(): void;
  onBack(): void;
  onDirtyChange?(dirty: boolean): void;
  onRetryPreparation?(): void;
  onEditStrategy?(): void;
  onRemoveStrategyLane?(lane: SmartCreationStrategyLane): void;
}

const objectiveKeys = {
  drive_growth: "driveGrowth",
  optimize_roas: "optimizeRoas",
  launch_product: "launchProduct",
  promotion_sales_boost: "promotion",
} as const;

function ReviewMetric({
  label,
  value,
  detail,
  metric,
}: {
  label: string;
  value: string;
  detail: React.ReactNode;
  metric: string;
}) {
  return (
    <div
      data-review-metric={metric}
      className="min-w-0 space-y-1 border-line py-0 pl-6 first:pl-0 not-last:border-r"
    >
      <p className="text-xs leading-[18px] text-muted-foreground">{label}</p>
      <p className="truncate text-2xl font-semibold leading-[30px] tracking-[-0.03em] tabular-nums">
        {value}
      </p>
      <div className="text-[11px] leading-[18px] text-muted-foreground">{detail}</div>
    </div>
  );
}

export function SmartCreationDraftReview({
  plan,
  workspace,
  disabled = false,
  applying = false,
  refreshingPreview = false,
  confirming = false,
  onApplyChanges,
  onApplyOptimistic,
  creationCapabilities,
  onRefreshPreview,
  onConfirm,
  onBack,
  onDirtyChange,
  onRetryPreparation,
  onEditStrategy,
  onRemoveStrategyLane,
}: SmartCreationDraftReviewProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const [tab, setTab] = useState("structure");
  const [search, setSearch] = useState("");
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [topologyOpen, setTopologyOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [namingOpen, setNamingOpen] = useState(false);
  const [namingDirty, setNamingDirty] = useState(false);
  const [entity, setEntity] = useState<
    SmartCreationReviewCampaign | SmartCreationReviewAdGroup | null
  >(null);
  const [targetGroup, setTargetGroup] = useState<SmartCreationReviewAdGroup | null>(null);
  const [entityDirty, setEntityDirty] = useState(false);
  const [targetDirty, setTargetDirty] = useState(false);
  const [budgetDirty, setBudgetDirty] = useState(false);
  const [aiDirty, setAiDirty] = useState(false);
  const [aiInvalid, setAiInvalid] = useState(false);
  const [deliveryDirty, setDeliveryDirty] = useState(false);
  const [planName, setPlanName] = useState(workspace.planSettings.name.effective);
  const [nameError, setNameError] = useState(false);
  const [capabilities, setCapabilities] = useState<{
    planId: string;
    shopId: string;
    values: readonly AiActionCapabilityV1[] | null;
    requestEpoch: number;
    bidBoundsSuggestion?: SmartCreationReviewWorkspace["bidBoundsSuggestion"];
  } | null>(null);
  const [capabilityEpoch, setCapabilityEpoch] = useState(0);
  const nameDirty = planName.trim() !== workspace.planSettings.name.effective;
  const dirty =
    entityDirty ||
    targetDirty ||
    budgetDirty ||
    aiDirty ||
    deliveryDirty ||
    nameDirty ||
    namingDirty;
  const actionBusy = disabled || applying || refreshingPreview || confirming;
  const mutationDisabled = actionBusy;
  const currency = useMemo(
    () =>
      new Intl.NumberFormat(i18n.resolvedLanguage, {
        style: "currency",
        currency: workspace.summary.currencyCode,
        maximumFractionDigits: 2,
      }),
    [i18n.resolvedLanguage, workspace.summary.currencyCode],
  );
  const percent = new Intl.NumberFormat(i18n.resolvedLanguage, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const campaigns = workspace.graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  const campaignKeys = new Set(campaigns.map((campaign) => campaign.ref.lineageKey));
  const groups = workspace.graph.adGroups.filter((group) =>
    campaignKeys.has(group.campaignLineageKey),
  );
  const groupKeys = new Set(groups.map((group) => group.ref.lineageKey));
  const targets = workspace.graph.targets.filter(
    (target) => !target.excluded.effective && groupKeys.has(target.adGroupLineageKey),
  );
  const promotedAsinsByGroup = new Map(
    groups.map((group) => [group.ref.lineageKey, group.products.map((product) => product.asin)]),
  );
  const exactScopes = new Map<string, { asin: string; strategies: Set<string> }>();
  for (const target of targets) {
    if (target.type !== "product" || (target.matchType ?? "exact") !== "exact") continue;
    for (const promotedAsin of promotedAsinsByGroup.get(target.adGroupLineageKey) ?? []) {
      const key = `${promotedAsin}:${target.asin}`;
      const scope = exactScopes.get(key) ?? { asin: target.asin, strategies: new Set<string>() };
      scope.strategies.add(target.strategyKind);
      exactScopes.set(key, scope);
    }
  }
  const overlappingAsins = [
    ...new Set(
      [...exactScopes.values()]
        .filter((scope) => scope.strategies.size > 1)
        .map((scope) => scope.asin),
    ),
  ].sort();
  const negatives = workspace.graph.negatives.filter((negative) =>
    groupKeys.has(negative.adGroupLineageKey),
  );
  const products: SmartCreationBrowserDraft["draft"]["products"] =
    plan?.draft.products ??
    [
      ...new Map(
        workspace.graph.adGroups.flatMap((group) =>
          group.products.map((product) => [product.sku, product] as const),
        ),
      ).values(),
    ].map((product) => ({
      ...product,
      variationGroupKey: product.asin,
      imageUrl: null,
      title: null,
      eligibility: "unknown" as const,
    }));
  const isAutomatic = (campaign: SmartCreationReviewCampaign) =>
    workspace.graph.targets.some(
      (target) =>
        target.type === "automatic" &&
        campaign.adGroupLineageKeys.includes(target.adGroupLineageKey),
    );
  const pendingLanes = workspace.pendingStrategyLanes ?? [];
  const blockingDiagnostics = workspace.diagnostics.filter((diagnostic) => diagnostic.blocking);
  const refreshBlockers = blockingDiagnostics.filter(
    (diagnostic) => diagnostic.code !== "preview_refresh_required",
  );
  const hasBudgetEdits = hasManualBudgetEdits(workspace);
  const displaySuggestion = matchingBudgetSuggestion(workspace);
  const smartAllocation =
    displaySuggestion?.every(
      (allocation) =>
        workspace.graph.campaigns.find(
          (campaign) => campaign.ref.lineageKey === allocation.ref.lineageKey,
        )?.dailyBudget.effective === allocation.allocation.amount,
    ) ?? false;
  const canConfirm =
    !aiInvalid &&
    !dirty &&
    !actionBusy &&
    !entity &&
    !targetGroup &&
    !budgetOpen &&
    !namingOpen &&
    workspace.status === "ready" &&
    blockingDiagnostics.length === 0 &&
    pendingLanes.length === 0;
  const planId = plan?.id;
  const shopId = plan?.shopId;
  const vendor = useIsVendorShop(shopId);
  const selectedProductsSummary = t(
    vendor
      ? "review.preview.selectedProductsSummaryAsin"
      : "review.preview.selectedProductsSummary",
    { count: products.length },
  );
  const capabilityScopeMatches =
    planId &&
    shopId &&
    workspace.aiActionPolicy?.source === "smart_creation" &&
    workspace.aiActionPolicy.planId === planId &&
    workspace.aiActionPolicy.shopId === shopId;
  const currentCapabilities =
    capabilityScopeMatches &&
    capabilities?.planId === planId &&
    capabilities.shopId === shopId &&
    capabilities.requestEpoch === capabilityEpoch
      ? capabilities.values
      : undefined;
  const capabilityStatus = !capabilityScopeMatches
    ? "unavailable"
    : currentCapabilities === undefined
      ? "loading"
      : currentCapabilities === null
        ? "unavailable"
        : "ready";

  useEffect(() => {
    setPlanName(workspace.planSettings.name.effective);
    setNameError(false);
  }, [workspace.planSettings.name.effective]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    // A retry starts a fresh request for the same scope.
    if (!planId || !shopId) return;
    let active = true;
    setCapabilities(null);
    void fetchSmartCreationActionCapabilities(planId, shopId)
      .then((response) => {
        if (active)
          setCapabilities({
            planId,
            shopId,
            requestEpoch: capabilityEpoch,
            bidBoundsSuggestion: response.bidBoundsSuggestion,
            values:
              response.planId === planId && response.shopId === shopId
                ? response.capabilities
                : null,
          });
      })
      .catch(() => {
        if (active)
          setCapabilities({ planId, shopId, requestEpoch: capabilityEpoch, values: null });
      });
    return () => {
      active = false;
    };
  }, [planId, shopId, capabilityEpoch]);
  async function applyChanges(changes: SmartCreationReviewEditChange[]) {
    if (mutationDisabled) return false;
    if (changes.length === 0) return true;
    return (await onApplyChanges(changes)) !== false;
  }
  async function changeMode(mode: ExecutionModeV1) {
    const policy = workspace.aiActionPolicy;
    if (dirty || mutationDisabled || policy?.source !== "smart_creation") return false;
    return (
      (await (onApplyOptimistic ?? applyChanges)([
        {
          operation: "set_ai_action_policy",
          policy: { ...policy, revision: policy.revision + 1, requestedExecutionMode: mode },
        },
      ])) !== false
    );
  }
  const currentEntity = entity
    ? ((entity.ref.kind === "campaign" ? workspace.graph.campaigns : workspace.graph.adGroups).find(
        (item) => item.ref.lineageKey === entity.ref.lineageKey,
      ) ?? entity)
    : null;
  const currentTargetGroup = targetGroup
    ? (workspace.graph.adGroups.find(
        (group) => group.ref.lineageKey === targetGroup.ref.lineageKey,
      ) ?? targetGroup)
    : null;

  return (
    <section
      className="smart-creation-preview flex h-full min-h-0 min-w-0 flex-col bg-surface text-ink"
      data-slot="smart-creation-draft-review"
      aria-busy={actionBusy}
    >
      <div className="paper-scrollbar min-h-0 flex-1 overflow-y-auto px-6 pt-6 pb-6">
        <div className="flex min-h-[52px] flex-wrap items-start justify-between gap-3 pb-5">
          <div className="flex min-h-8 min-w-0 items-center gap-2.5">
            <h2
              className="truncate text-base font-semibold leading-[22px]"
              title={workspace.planSettings.name.effective}
            >
              {workspace.planSettings.name.effective}
            </h2>
            <Badge variant="neutral">{t("review.preview.draft")}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setProductsOpen(true)}>
              <IconPackage className="size-3.5" />
              {selectedProductsSummary}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTopologyOpen(true)}>
              <IconCube data-icon="inline-start" />
              {t("review.preview.topology")}
            </Button>
          </div>
        </div>
        <div
          data-review-confirmation-summary
          className="grid grid-cols-2 gap-y-4 pb-5 lg:grid-cols-[minmax(0,336fr)_repeat(3,minmax(0,274fr))]"
        >
          <ReviewMetric
            metric="budget"
            label={t("review.preview.totalDailyBudget")}
            value={currency.format(
              campaigns.reduce((sum, campaign) => sum + campaign.dailyBudget.effective, 0),
            )}
            detail={
              <button
                type="button"
                className="cursor-pointer rounded-sm text-left hover:text-ink focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-default disabled:opacity-50"
                disabled={actionBusy || dirty}
                onClick={() => setBudgetOpen(true)}
              >
                {t(
                  smartAllocation || !hasBudgetEdits
                    ? "review.preview.smartAllocation"
                    : "review.preview.manualAllocation",
                )}{" "}
                · {t("review.preview.viewAllocation")}
              </button>
            }
          />
          <ReviewMetric
            metric="campaigns"
            label={t("review.summary.campaigns")}
            value={String(campaigns.length)}
            detail={t("review.preview.campaignBreakdown", {
              automatic: campaigns.filter((campaign) => isAutomatic(campaign)).length,
              manual: campaigns.filter((campaign) => !isAutomatic(campaign)).length,
            })}
          />
          <ReviewMetric
            metric="adGroups"
            label={t("review.summary.adGroups")}
            value={String(groups.length)}
            detail={t("review.preview.productAds", {
              count: groups.reduce((sum, group) => sum + group.products.length, 0),
            })}
          />
          <ReviewMetric
            metric="targets"
            label={t("review.preview.targetsMetric")}
            value={String(targets.length)}
            detail={t("review.preview.negativeCount", { count: negatives.length })}
          />
        </div>
        <div className="flex min-h-10 flex-wrap items-center gap-x-5 gap-y-2 pb-2 text-xs leading-[18px]">
          {plan?.draft.objective ? (
            <span className="font-medium">
              {t(`objectives.${objectiveKeys[plan.draft.objective]}`)}
            </span>
          ) : null}
          {plan?.draft.targetAcos != null ? (
            <span className="text-muted-foreground">
              {t("strategy.context.targetAcos")} {percent.format(plan.draft.targetAcos)}
            </span>
          ) : null}
          {plan ? (
            <span className="text-muted-foreground">
              {t("strategy.context.personality")}{" "}
              {plan.draft.aiPersonality ??
                (plan.draft.riskPreference === "conservative"
                  ? 1
                  : plan.draft.riskPreference === "aggressive"
                    ? 5
                    : 3)}
            </span>
          ) : null}
          <span className="text-muted-foreground">
            {t(`structure.${workspace.suggestedDraft.structure}`)}{" "}
            {t("review.preview.structureSuffix")}
          </span>
          <div className="ml-auto">
            <ReviewAiModeSelector
              optimistic={Boolean(onApplyOptimistic)}
              value={
                workspace.aiActionPolicy?.source === "smart_creation"
                  ? (workspace.aiActionPolicy.requestedExecutionMode ?? "recommendation")
                  : "recommendation"
              }
              disabled={mutationDisabled || dirty || !capabilityScopeMatches}
              onChange={changeMode}
            />
          </div>
        </div>
        <Tabs
          value={tab}
          onValueChange={(next) => {
            if (!dirty) setTab(String(next));
          }}
          className="gap-0"
        >
          <TabsList
            className="w-full justify-start gap-7 border-line [&_[data-slot=tab-indicator]]:bg-ink"
            aria-label={t("review.preview.tabs")}
          >
            <TabsTab
              className="h-11 px-1 text-[13px]"
              value="structure"
              disabled={dirty && tab !== "structure"}
            >
              {t("review.preview.structureTab")}
            </TabsTab>
            <TabsTab
              className="h-11 px-1 text-[13px]"
              value="delivery"
              disabled={dirty && tab !== "delivery"}
            >
              {t("review.preview.deliveryTab")}
            </TabsTab>
            <TabsTab className="h-11 px-1 text-[13px]" value="ai" disabled={dirty && tab !== "ai"}>
              {t("review.preview.aiTab")}
            </TabsTab>
          </TabsList>
          <TabsPanel value="structure">
            {pendingLanes.length > 0 ? (
              <div className="grid gap-3 pt-5" data-preview-pending-details>
                {pendingLanes.map((lane) => (
                  <section
                    key={lane}
                    className="flex flex-wrap items-start justify-between gap-3 rounded-[10px] border border-line p-4"
                  >
                    <div className="space-y-1">
                      <h3 className="text-[13px] font-semibold">
                        {t(`strategy.${lane.split("_")[0]}`)} · {t("review.preview.pendingLane")}
                      </h3>
                      <p className="text-xs leading-5 text-muted-foreground">
                        {workspace.strategyPreparation
                          ?.filter((result) => result.lane === lane && result.status !== "ready")
                          .map((result) =>
                            t(`strategy.deferred.reasons.${result.reason}`, {
                              defaultValue: t("strategy.deferred.noRelevantTargets"),
                            }),
                          )
                          .join(" · ") || t("strategy.deferred.previewPending")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {onEditStrategy ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={mutationDisabled || dirty}
                          onClick={onEditStrategy}
                        >
                          {t("strategy.deferred.editTargets")}
                        </Button>
                      ) : null}
                      {onRemoveStrategyLane && lane !== "automatic" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={mutationDisabled || dirty}
                          onClick={() => onRemoveStrategyLane(lane)}
                        >
                          {t("strategy.deferred.removeIntent")}
                        </Button>
                      ) : null}
                    </div>
                  </section>
                ))}
              </div>
            ) : null}
            <ReviewCampaignTable
              workspace={workspace}
              planId={plan?.id}
              vendor={vendor}
              query={search.trim().toLocaleLowerCase(i18n.resolvedLanguage)}
              onQueryChange={setSearch}
              disabled={
                mutationDisabled ||
                nameDirty ||
                deliveryDirty ||
                aiDirty ||
                entityDirty ||
                targetDirty ||
                budgetDirty
              }
              onApply={applyChanges}
              onApplyOptimistic={onApplyOptimistic}
              onInspect={(campaign) => setEntity(campaign)}
              onTargets={setTargetGroup}
              actions={
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={mutationDisabled || dirty}
                    onClick={() => setNamingOpen(true)}
                  >
                    <IconTypography data-icon="inline-start" />
                    {t("review.targetSelection.namingTemplates")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={mutationDisabled || dirty || !workspace.graph.adGroups.length}
                    onClick={() => setTargetGroup(groups[0] ?? workspace.graph.adGroups[0] ?? null)}
                  >
                    <IconTarget data-icon="inline-start" />
                    {t("review.targetSelection.editTargets")}
                  </Button>
                </div>
              }
            />
          </TabsPanel>
          <TabsPanel value="delivery" className="pt-6">
            <ReviewDeliverySettings
              plan={plan}
              workspace={workspace}
              disabled={
                mutationDisabled ||
                entityDirty ||
                targetDirty ||
                budgetDirty ||
                aiDirty ||
                nameDirty
              }
              onApply={applyChanges}
              onDirtyChange={setDeliveryDirty}
              planNameField={
                <Field className="gap-[7px]" invalid={nameError}>
                  <FieldLabel htmlFor="review-plan-name">{t("review.summary.planName")}</FieldLabel>
                  <Input
                    id="review-plan-name"
                    value={planName}
                    disabled={mutationDisabled || deliveryDirty}
                    maxLength={128}
                    onChange={(event) => {
                      setPlanName(event.target.value);
                      setNameError(false);
                    }}
                  />
                  {nameError ? (
                    <FieldError match>{t("review.summary.invalidName")}</FieldError>
                  ) : null}
                  {nameDirty ? (
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={mutationDisabled}
                        onClick={() => setPlanName(workspace.planSettings.name.effective)}
                      >
                        {t("review.parity.cancel")}
                      </Button>
                      <Button
                        size="sm"
                        disabled={mutationDisabled || deliveryDirty}
                        onClick={() => {
                          const name = planName.trim();
                          if (!name || name.length > 128) {
                            setNameError(true);
                            return;
                          }
                          void applyChanges([
                            { operation: "set_plan_name", ref: workspace.planSettings.ref, name },
                          ]);
                        }}
                      >
                        {t("review.preview.saveChanges")}
                      </Button>
                    </div>
                  ) : null}
                </Field>
              }
            />
          </TabsPanel>
          <TabsPanel value="ai" className="pt-1">
            <ReviewAiActionSpace
              immediate
              bidBoundsSuggestion={
                workspace.bidBoundsSuggestion ??
                (capabilityScopeMatches ? capabilities?.bidBoundsSuggestion : undefined)
              }
              onInvalidChange={setAiInvalid}
              variant="preview"
              currencyCode={workspace.summary.currencyCode}
              policy={workspace.aiActionPolicy}
              capabilities={currentCapabilities ?? undefined}
              capabilityStatus={capabilityStatus}
              disabled={mutationDisabled || workspace.status !== "ready"}
              onApply={applyChanges}
              onDirtyChange={setAiDirty}
              onRetryCapabilities={() => setCapabilityEpoch((epoch) => epoch + 1)}
            />
          </TabsPanel>
        </Tabs>
      </div>
      <footer
        className="flex min-h-[100px] shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-line bg-table-footer px-6 py-[18px]"
        data-review-footer
      >
        <div className="mr-auto flex min-w-0 flex-wrap items-center gap-2 text-xs">
          {overlappingAsins.length > 0 ? (
            <Popover>
              <PopoverTrigger render={<Button size="sm" variant="ghost" />}>
                <IconInfoCircle className="size-4" />
                {t("review.preview.overlapSummary", { count: overlappingAsins.length })}
              </PopoverTrigger>
              <PopoverPopup
                className="smart-creation-preview max-w-96 text-xs leading-5"
                data-review-product-overlap
              >
                {t("review.exactProductOverlap", {
                  count: overlappingAsins.length,
                  asins: overlappingAsins.join(", "),
                })}
              </PopoverPopup>
            </Popover>
          ) : null}
          {pendingLanes.length > 0 ? (
            <>
              <IconAlertTriangle className="size-4 text-warning-foreground" />
              <span>{t("strategy.deferred.pendingSummary", { count: pendingLanes.length })}</span>
              {onRetryPreparation ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={mutationDisabled || dirty}
                  onClick={onRetryPreparation}
                >
                  <IconRefresh />
                  {t("strategy.deferred.retryPreparation")}
                </Button>
              ) : null}
            </>
          ) : null}
          {workspace.status === "preview_refresh_required" ? (
            <>
              <IconAlertTriangle className="size-4" />
              <span>{t("review.status.preview_refresh_required.title")}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={actionBusy || dirty || refreshBlockers.length > 0}
                loading={refreshingPreview}
                onClick={() => onRefreshPreview()}
              >
                <IconRefresh />
                {t("review.actions.refreshPreview")}
              </Button>
            </>
          ) : null}
          {refreshBlockers.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setDiagnosticsOpen(true)}>
              <IconAlertTriangle className="size-4" />
              {t("review.preview.checkRequired", { count: refreshBlockers.length })}
            </Button>
          ) : null}
        </div>
        <Button variant="outline" disabled={actionBusy || dirty} onClick={onBack}>
          {t("review.preview.backStructure")}
        </Button>
        <Button disabled={!canConfirm} loading={confirming} onClick={() => onConfirm()}>
          {t("review.preview.createCampaigns")}
        </Button>
      </footer>
      {currentEntity ? (
        <ReviewEntitySheet
          key={currentEntity.ref.lineageKey}
          workspace={workspace}
          entity={currentEntity}
          capabilities={creationCapabilities}
          disabled={actionBusy}
          onApply={applyChanges}
          onClose={() => setEntity(null)}
          onDirtyChange={setEntityDirty}
        />
      ) : null}
      <ReviewNamingRules
        workspace={workspace}
        open={namingOpen}
        onOpenChange={setNamingOpen}
        onDirtyChange={setNamingDirty}
        disabled={mutationDisabled}
        onApply={applyChanges}
      />
      {currentTargetGroup ? (
        <ReviewTargetDialog
          workspace={workspace}
          plan={plan}
          group={currentTargetGroup}
          capabilities={creationCapabilities}
          disabled={actionBusy}
          onApply={applyChanges}
          onClose={() => {
            setTargetDirty(false);
            setTargetGroup(null);
          }}
          onGroupChange={setTargetGroup}
          onDirtyChange={setTargetDirty}
          onEditGroup={
            targetDirty
              ? undefined
              : (group) => {
                  setTargetGroup(null);
                  setEntity(group);
                }
          }
        />
      ) : null}
      <ReviewBudgetDialog
        open={budgetOpen}
        workspace={workspace}
        capabilities={creationCapabilities}
        disabled={actionBusy}
        onApply={applyChanges}
        onClose={() => setBudgetOpen(false)}
        onDirtyChange={setBudgetDirty}
      />
      <Dialog open={productsOpen} onOpenChange={setProductsOpen}>
        <DialogPopup className="smart-creation-preview max-w-[672px]">
          <DialogHeader>
            <DialogTitle>{selectedProductsSummary}</DialogTitle>
            <DialogDescription>{t("review.preview.productsDescription")}</DialogDescription>
          </DialogHeader>
          <ul className="paper-scrollbar max-h-[60vh] overflow-y-auto px-6 pb-6">
            {products.map((product) => (
              <li
                key={product.sku}
                className="flex items-center gap-3 border-b border-line py-3 last:border-0"
              >
                <ProductThumbnail
                  imageUrl={product.imageUrl}
                  label={product.title ?? product.asin}
                  asin={product.asin}
                  sku={vendor ? undefined : product.sku}
                />
                <div className="min-w-0 text-xs">
                  <p className="truncate font-medium">{product.title ?? product.asin}</p>
                  <p className="mt-1 text-muted-foreground">
                    {vendor
                      ? `ASIN: ${product.asin}`
                      : `ASIN: ${product.asin} · SKU: ${product.sku}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </DialogPopup>
      </Dialog>
      <Dialog open={topologyOpen} onOpenChange={setTopologyOpen}>
        <DialogPopup className="smart-creation-preview max-w-[800px]">
          <DialogHeader>
            <DialogTitle>{t("review.preview.topologyTitle")}</DialogTitle>
            <DialogDescription>{t("review.preview.topologyDescription")}</DialogDescription>
          </DialogHeader>
          <ReviewStructureGraph workspace={workspace} />
        </DialogPopup>
      </Dialog>
      <Dialog open={diagnosticsOpen} onOpenChange={setDiagnosticsOpen}>
        <DialogPopup className="smart-creation-preview max-w-[672px]">
          <DialogHeader>
            <DialogTitle>{t("review.preview.diagnosticsTitle")}</DialogTitle>
            <DialogDescription>{t("review.preview.diagnosticsDescription")}</DialogDescription>
          </DialogHeader>
          <ul className="paper-scrollbar max-h-[60vh] space-y-3 overflow-y-auto px-6 pb-6">
            {refreshBlockers.map((diagnostic) => (
              <li
                key={`${diagnostic.code}:${diagnostic.entity?.lineageKey ?? "plan"}:${diagnostic.field ?? ""}:${diagnostic.message}`}
                className="rounded-lg border border-line p-3"
              >
                <p className="text-xs leading-5">{diagnostic.message}</p>
                {diagnostic.entity ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      const ref = diagnostic.entity;
                      if (!ref) return;
                      if (ref.kind === "plan") {
                        setTab("delivery");
                        setDiagnosticsOpen(false);
                        return;
                      }
                      const campaign = workspace.graph.campaigns.find(
                        (item) => item.ref.lineageKey === ref.lineageKey,
                      );
                      if (campaign) setEntity(campaign);
                      else {
                        const target =
                          workspace.graph.targets.find(
                            (item) => item.ref.lineageKey === ref.lineageKey,
                          ) ??
                          workspace.graph.negatives.find(
                            (item) => item.ref.lineageKey === ref.lineageKey,
                          );
                        const group = workspace.graph.adGroups.find(
                          (item) =>
                            item.ref.lineageKey === ref.lineageKey ||
                            item.ref.lineageKey === target?.adGroupLineageKey,
                        );
                        if (group) setTargetGroup(group);
                      }
                      setDiagnosticsOpen(false);
                    }}
                  >
                    {t("review.preview.reviewIssue")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </DialogPopup>
      </Dialog>
    </section>
  );
}
